import { ImageResourceItem, Inject, ISimpleControllerConfiguration, SimpleBaseController } from "@denishussar/html5-game-engine";
import { Point } from "pixi.js";
import { easeInOutCubic, Easing } from "../../Common/Easing";
import { GameLogic } from "../../Logic/GameLogic";
import { GameStateEnum } from "../../Models/GameModel";
import { GamePictureController, IGamePictureControllerOption } from "../Game/GamePictureController";
import { ITopBarMenuControllerOptions, TopBarMenuController } from "./TopBarMenuController";

export interface IAchievementScreenControllerOptions {

}

export class AchievementScreenController extends SimpleBaseController {
    $model: any;

    protected frameSize: number = 248;
    protected frameSizeInitialized = 248;
    
    protected picturecContainer: PIXI.Container;
    protected frame: PIXI.mesh.NineSlicePlane;
    protected picture: GamePictureController;

    protected _width = 100;
    protected _height = 100;
    protected _scale = 1;

    protected easings: Array<Easing> = [];

    constructor(
        @Inject protected options: IAchievementScreenControllerOptions,
        @Inject protected gameLogic: GameLogic,
    ) {
        super('AchievementScreenController');
    }

    protected getConfiguration(): ISimpleControllerConfiguration {
        const resources = {
            'frame': new ImageResourceItem('frame', 'picture_frame.png'),
        };
        return <ISimpleControllerConfiguration>{
            resources,
            modelEvents: {
                onTextureCreated: e => {
                    this.handleTextureCreated();
                },
            },
            childControllers: {
                picture: {
                    controller: GamePictureController,
                    model: this.$model,
                    options: <IGamePictureControllerOption>{
                        fillColorsAfterInitializtion: true
                    }
                },
                topBarMenu: {
                    controller: TopBarMenuController,
                    model: this.$model,
                    options: <ITopBarMenuControllerOptions>{
                        showExitButton: true,
                        showCoins: true,
                        onClickExit: (status: boolean) => {
                            this.gameLogic.setState(GameStateEnum.main);
                        },
                    }
                },
            },
        };
    }

    protected onInitialize(): void {
        const topBar = this.$childControllers.get<GamePictureController>('topBarMenu');

        this.picturecContainer = new PIXI.Container();
        this.display.addChildAt(this.picturecContainer, this.display.getChildIndex(topBar.display) - 1);
    }

    protected onUpdate(frameRate: number) {
        this.easings.forEach((easing) => {
            easing.update(frameRate);
        });
    }

    protected onResize(width: number, height: number, scale: number) {
        this._width = width;
        this._height = height;
        this._scale = scale;

        if (!this.frame) {
            if (this.$model.saveTexture) {
                this.handleTextureCreated();
            }
            return;
        }
    }

    protected handleTextureCreated(): void {
        const margin = 64;
        const pictureSize = {
            width: this.gameLogic.getPictureSize().width * this.gameLogic.getPixelSize(),
            height: this.gameLogic.getPictureSize().height * this.gameLogic.getPixelSize(),
        };

        if (!this.frame) {
            const frameTexture = this.getResources().filter((res) => res.name === 'frame')[0].getTexture();
            this.frame = new PIXI.mesh.NineSlicePlane(frameTexture, this.frameSize, this.frameSize, this.frameSize, this.frameSize);
            this.frame.interactive = false;
            this.picturecContainer.addChild(this.frame);
        }
        this.frame.width = pictureSize.width + 2 * margin;
        this.frame.height = pictureSize.height + 2 * margin;
        this.frame.position.set(-this.frame.width / 2, -this.frame.height / 2);
        this.frameSizeInitialized = this.frame.width;

        if (!this.picture) {
            this.picture = this.$childControllers.get<GamePictureController>('picture');
            this.picturecContainer.addChild(this.picture.display);
        }
        this.picture.display.position.set(this.frame.position.x + margin, this.frame.position.y + margin);

        this.center();
        this.animatePictureZooming(this.getMinScale(), new PIXI.Point(0, 0));
    }

    protected center(): void {
        if (!this.frame) {
            return;
        }

        this.picturecContainer.position.set(0, 0);
    }

    protected animatePictureZooming(endScale: number, endPosition: Point): void {
        this.easings.splice(0);

        const timeout = 600;
        const startScale = this.picturecContainer.scale.y;
        
        const startPosition = this.picturecContainer.position.clone();

        const easing = new Easing(
            [startScale, startPosition.x, startPosition.y],
            [endScale, endPosition.x, endPosition.y],
            timeout,
            easeInOutCubic
        );

        easing.onComplete = () => {
            for (let i = 0; i < this.easings.length; i++) {
                if (this.easings[i] === easing) {
                    this.easings.splice(i, 1);
                    break;
                }
            }
        };
        easing.onStep = (values: Array<number>) => {
            if (this.picturecContainer) {
                this.picturecContainer.scale.set(values[0]);
                this.picturecContainer.position.set(values[1], values[2]);
            }
        };
        this.easings.push(easing);
    }

    protected getMinScale(): number {
        const minSize = Math.min(this._width, this._height);
        const scale = minSize / this.frameSizeInitialized - .05;

        return scale / this._scale;
    }
}
